import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { userManager } from '../components/OidcAuthProvider';
import { PATHS } from '../constants/Paths.constants';
import { isLogoutInProgress } from '../utils/Auth.util';
import { getCurrentUser } from '../services/UserService';

/**
 * Dedicated OIDC callback page.
 * Keycloak redirects here after login (redirect_uri = /auth/callback).
 * Processes the authorization code, then redirects to the original page.
 */
export function OidcCallbackPage() {
  const navigate = useNavigate();
  const handled = useRef(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (handled.current) return;
    handled.current = true;

    if (isLogoutInProgress()) {
      navigate(PATHS.LOGOUT, { replace: true });
      return;
    }

    const handleCallback = async () => {
      try {
        const user = await userManager.signinRedirectCallback();

        // Ensure backend user sync is completed before entering app routes.
        await getCurrentUser();

        // Full-page redirect intentionally reloads the app after callback sync.
        const returnTo = (user?.state as any)?.returnTo || PATHS.DASHBOARD;
        navigate(returnTo, { replace: true });
      } catch (err) {
        console.error('[OIDC] Callback error:', err);
        setError('Login failed. Please try again.');
      }
    };

    void handleCallback();
  }, [navigate]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button
            className="text-purple-700 underline"
            onClick={() => userManager.signinRedirect()}
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-white">
      <p className="text-gray-500">Signing in...</p>
    </div>
  );
}
